import React, { useState } from 'react'
import { orderBy } from 'lodash'

import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { Dialog, DialogContent } from 'common/components/Dialog/Dialog'
import { CreateDialogBanner } from 'common/components/CreateDialogBanner/CreateDialogBanner'
import { DeleteDialogBanner } from 'common/components/DeleteDialogBanner/DeleteDialogBanner'
import { EmptyStateContainer } from 'common/components/EmptyStateContainer/EmptyStateContainer'
import { Skeleton } from 'common/components/Skeleton/Skeleton'
import { useAuth } from 'modules/Auth/hooks/useAuth'

import {
  useListenCommunityReportsQuery,
  useListenBannedMembersQuery,
  useFetchCommunityMemberProfileQuery,
  useFetchReportedTargetQuery,
  useFetchIsOrgAdminQuery,
  useSetReportStatusMutation,
  useSetMemberBannedMutation,
  useDeleteCommunityPostMutation,
  useDeleteCommunityReplyMutation,
} from '../communityApi'
import { formatBadgeCount, normalizeCommunityRecords } from '../communityUtils'
import BannedMembers from './BannedMembers'
import PostAvatar from './PostAvatar'
import formatFeedTime from './formatFeedTime'

// Identifies the content a report points at. Deleting a post also deletes its
// replies, so a post's key doubles as the key for reports about those replies.
const targetKey = (report) => (report.targetType === 'reply' ? `${report.postId}/${report.replyId}` : report.postId)

const VIEW_REPORTS = 'reports'
const VIEW_BANNED = 'banned'
const VIEW_HANDLED = 'handled'

// The coach's moderation surface, in three views behind pills like the feed's
// tabs: open member-filed reports (with the content they point at), the
// banned-members list, and the reports already handled. Reports come from the
// app's "Report post/reply" action; bans made here behave exactly like bans
// made in the app (the member's posts are hidden for everyone immediately).
export function ModerationTab({ coachOrgId }) {
  const { createAlert } = useAlert()
  const [view, setView] = useState(VIEW_REPORTS)

  const { data: reportsData } = useListenCommunityReportsQuery({ coachOrgId }, { skip: !coachOrgId })
  const reportsLoading = reportsData === undefined || reportsData?.isLoading
  const { data: bannedData } = useListenBannedMembersQuery({ coachOrgId }, { skip: !coachOrgId })
  const bannedLoading = bannedData === undefined || bannedData?.isLoading

  const reports = Object.values(normalizeCommunityRecords(reportsLoading ? {} : reportsData))
  const openReports = orderBy(
    reports.filter((r) => r?.status === 'open'),
    [(r) => r?.createdAt || 0],
    ['desc']
  )
  const closedReports = orderBy(
    reports.filter((r) => r?.status !== 'open'),
    [(r) => r?.createdAt || 0],
    ['desc']
  )
  const bannedIds = bannedLoading ? [] : Object.keys(bannedData || {}).filter((id) => bannedData[id])
  const bannedIdSet = new Set(bannedIds)

  const [setReportStatus] = useSetReportStatusMutation()
  const [setMemberBanned] = useSetMemberBannedMutation()
  const [deletePost] = useDeleteCommunityPostMutation()
  const [deleteReply] = useDeleteCommunityReplyMutation()

  // One controlled dialog for the whole tab, same pattern as the feed's
  // "Mark as done?" confirm: { kind: 'dismiss'|'delete'|'ban'|'unban', ... }.
  const [confirm, setConfirm] = useState(null)
  const [confirmLoading, setConfirmLoading] = useState(false)

  // Content deleted during this visit. A row's preview comes from a one-shot
  // fetch, so it still holds the post after we delete it; without this the row
  // keeps offering Delete and the second click fails — Firebase refuses a delete
  // of a record that is already gone.
  const [deletedTargets, setDeletedTargets] = useState({})

  const finish = (result, successText) => {
    if (result?.error || result?.data?.error) {
      createAlert({ text: 'Something went wrong. Please try again.', type: 'danger' })
      return false
    }
    createAlert({ text: successText, type: 'success' })
    return true
  }

  const handleConfirm = async () => {
    if (!confirm) return
    setConfirmLoading(true)
    if (confirm.kind === 'dismiss') {
      const result = await setReportStatus({ coachOrgId, reportId: confirm.report.id, status: 'dismissed' })
      finish(result, 'Report dismissed')
    } else if (confirm.kind === 'delete') {
      const { report } = confirm
      const result =
        report.targetType === 'reply'
          ? await deleteReply({ coachOrgId, postId: report.postId, replyId: report.replyId })
          : await deletePost({ coachOrgId, postId: report.postId })
      if (finish(result, 'Content deleted')) {
        setDeletedTargets((prev) => ({ ...prev, [targetKey(report)]: true }))
        await setReportStatus({ coachOrgId, reportId: report.id, status: 'resolved' })
      }
    } else if (confirm.kind === 'ban') {
      const { report } = confirm
      const result = await setMemberBanned({ coachOrgId, userId: report.targetAuthorId, isBanned: true })
      if (finish(result, 'Member banned')) {
        await setReportStatus({ coachOrgId, reportId: report.id, status: 'resolved' })
      }
    } else if (confirm.kind === 'unban') {
      const result = await setMemberBanned({ coachOrgId, userId: confirm.userId, isBanned: false })
      finish(result, 'Member unbanned')
    }
    setConfirmLoading(false)
    setConfirm(null)
  }

  // Same look as the feed's tab pills.
  const pillClasses = (isActive) =>
    `px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap shrink-0 transition-colors ${
      isActive ? 'bg-tGreen text-white' : 'bg-white text-tGray-dark hover:bg-gray-100'
    }`
  // Counts only once their list has loaded, or they jump from nothing to a number.
  const badge = (count) => (count > 0 ? ` · ${formatBadgeCount(count)}` : '')

  const renderReports = (list) =>
    list.map((report) => (
      <ReportRow
        key={report.id}
        report={report}
        coachOrgId={coachOrgId}
        onAction={setConfirm}
        bannedIdSet={bannedIdSet}
        deletedTargets={deletedTargets}
      />
    ))

  return (
    <div className='mb-20'>
      <div className='flex flex-wrap items-center gap-2 mb-6'>
        <button className={pillClasses(view === VIEW_REPORTS)} onClick={() => setView(VIEW_REPORTS)}>
          Reports{!reportsLoading && badge(openReports.length)}
        </button>
        <button className={pillClasses(view === VIEW_BANNED)} onClick={() => setView(VIEW_BANNED)}>
          Banned members{!bannedLoading && badge(bannedIds.length)}
        </button>
        <button className={pillClasses(view === VIEW_HANDLED)} onClick={() => setView(VIEW_HANDLED)}>
          Handled
        </button>
      </div>

      {view === VIEW_REPORTS && (
        <section>
          {reportsLoading && <ReportRowSkeleton />}
          {renderReports(openReports)}
          {!reportsLoading && openReports.length === 0 && (
            <EmptyStateContainer text='No open reports' subtext='Members can report posts and replies from the app.' />
          )}
        </section>
      )}

      {view === VIEW_BANNED && (
        <section>
          <BannedMembers
            coachOrgId={coachOrgId}
            bannedIds={bannedLoading ? undefined : bannedIds}
            onUnban={setConfirm}
          />
        </section>
      )}

      {view === VIEW_HANDLED && (
        <section>
          {reportsLoading && <ReportRowSkeleton />}
          {renderReports(closedReports)}
          {!reportsLoading && closedReports.length === 0 && (
            <EmptyStateContainer text='No handled reports' subtext='Reports you dismiss or act on move here.' />
          )}
        </section>
      )}

      <Dialog open={Boolean(confirm)} setOpen={(open) => !open && setConfirm(null)}>
        <DialogContent>
          {confirm?.kind === 'dismiss' && (
            <CreateDialogBanner
              titleText='Dismiss report?'
              createBtnText='Dismiss'
              text='The reported content stays up and the report is marked handled.'
              handleCreate={handleConfirm}
              loading={confirmLoading}
            />
          )}
          {confirm?.kind === 'delete' && (
            <DeleteDialogBanner
              titleText={confirm.report.targetType === 'reply' ? 'Delete this reply?' : 'Delete this post?'}
              text='It disappears from the feed for every member. The report is marked handled.'
              subtext="This can't be undone."
              handleDelete={handleConfirm}
              loading={confirmLoading}
            />
          )}
          {confirm?.kind === 'ban' && (
            <DeleteDialogBanner
              titleText={`Ban ${confirm.name || 'this member'}?`}
              text='Their posts and replies will be hidden from the community. They can no longer post.'
              subtext='You can unban them here at any time, which restores their posts.'
              deleteText='Ban'
              handleDelete={handleConfirm}
              loading={confirmLoading}
            />
          )}
          {confirm?.kind === 'unban' && (
            <CreateDialogBanner
              titleText={`Unban ${confirm.name || 'this member'}?`}
              createBtnText='Unban'
              text='They can post again and their hidden posts will reappear.'
              handleCreate={handleConfirm}
              loading={confirmLoading}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Rows fetch the member profile after the list arrives; without a skeleton they
// flash as "Unknown member" with a placeholder avatar.
function ReportRowSkeleton() {
  return (
    <div className='flex items-start bg-white rounded-2xl p-5 mb-3 h-[120px]'>
      <Skeleton className='!w-10 !h-10 shrink-0 rounded-full' />
      <div className='ml-3 flex-1'>
        <Skeleton className='w-40' />
        <Skeleton className='w-full mt-6' />
        <Skeleton className='w-2/5 mt-4' />
      </div>
    </div>
  )
}

// One report: who wrote what, who reported it and why, and the actions.
// Handled reports keep theirs — dismissing leaves the content up and the member
// active. Each action shows only while it still means something.
function ReportRow({ report, coachOrgId, onAction, bannedIdSet, deletedTargets }) {
  const { userId } = useAuth()
  const { data: authorProfile } = useFetchCommunityMemberProfileQuery(
    { coachOrgId, userId: report.targetAuthorId },
    { skip: !report.targetAuthorId }
  )
  const { data: reporterProfile } = useFetchCommunityMemberProfileQuery(
    { coachOrgId, userId: report.reporterId },
    { skip: !report.reporterId }
  )
  const { data: target } = useFetchReportedTargetQuery({
    coachOrgId,
    targetType: report.targetType,
    postId: report.postId,
    replyId: report.replyId,
  })

  // True when the author is on the org's admin list, which makes them a coach.
  const { data: authorIsOrgAdmin } = useFetchIsOrgAdminQuery(
    { coachOrgId, userId: report.targetAuthorId },
    { skip: !coachOrgId || !report.targetAuthorId }
  )

  // null = deleted or missing; undefined = still loading (or skipped).
  const isLoading =
    (Boolean(report.targetAuthorId) && authorIsOrgAdmin === undefined) ||
    (Boolean(report.targetAuthorId) && authorProfile === undefined) ||
    (Boolean(report.reporterId) && reporterProfile === undefined) ||
    target === undefined
  const authorName = authorProfile?.displayName || 'Unknown member'
  const reporterName = reporterProfile?.displayName || 'A member'
  const contentGone = target === null || Boolean(deletedTargets[targetKey(report)] || deletedTargets[report.postId])
  const isHandled = report.status !== 'open'
  const authorIsBanned = Boolean(report.targetAuthorId) && bannedIdSet.has(report.targetAuthorId)

  const canDismiss = !isHandled
  const canDelete = !contentGone
  // A member can report a coach's post like any other, but a coach is never
  // offered a ban, the same rule the feed follows. Three things can say the
  // author is a coach. The post or reply carries a "written by a coach" flag,
  // but that goes when the content is deleted. The org's admin list covers every
  // coach in the org and outlives the content. The signed-in user's own id
  // covers Trybe staff looking at an org whose admin list they are not on.
  const authorIsCoach =
    target?.authorIsCoach === true ||
    authorIsOrgAdmin === true ||
    (Boolean(report.targetAuthorId) && report.targetAuthorId === userId)
  const canBan = Boolean(report.targetAuthorId) && !authorIsBanned && !authorIsCoach

  if (isLoading) {
    return <ReportRowSkeleton />
  }

  return (
    <div className='relative group bg-white rounded-2xl p-5 mb-3'>
      <div className='flex items-start'>
        <PostAvatar name={authorName} picture={authorProfile?.picture} color={authorProfile?.color} size={40} />
        <div className='ml-3 min-w-0 flex-1'>
          <div className='flex items-baseline'>
            <span className='font-semibold text-tBlack'>{authorName}</span>
            <span className='text-xs text-gray-400 ml-2'>
              {report.targetType === 'reply' ? 'reply' : 'post'} · reported {formatFeedTime(report.createdAt)}
            </span>
            {report.status !== 'open' && (
              <span className='text-xs font-semibold text-gray-400 uppercase ml-auto'>{report.status}</span>
            )}
          </div>
          {contentGone ? (
            <p className='text-gray-400 italic mt-1'>Content no longer available</p>
          ) : (
            <p className='text-tBlack mt-1 whitespace-pre-wrap break-words'>{target?.text || ''}</p>
          )}
          <p className='text-sm text-gray-500 mt-2'>
            Reported by {reporterName}
            {report.reason ? (
              <>
                {': '}
                <span className='italic'>“{report.reason}”</span>
              </>
            ) : null}
          </p>
        </div>
      </div>
      {onAction && (canDismiss || canDelete || canBan) && (
        <div className='absolute top-3 right-3 hidden group-hover:flex items-center divide-x divide-gray-200 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden'>
          {canDismiss && (
            <button
              className='px-3 py-1.5 text-sm font-semibold text-tGray-dark hover:text-tGreen'
              onClick={() => onAction({ kind: 'dismiss', report })}
            >
              Dismiss
            </button>
          )}
          {canDelete && (
            <button
              className='px-3 py-1.5 text-sm font-semibold text-tGray-dark hover:text-red-500'
              onClick={() => onAction({ kind: 'delete', report })}
            >
              Delete {report.targetType === 'reply' ? 'reply' : 'post'}
            </button>
          )}
          {canBan && (
            <button
              className='px-3 py-1.5 text-sm font-semibold text-tGray-dark hover:text-red-500'
              onClick={() => onAction({ kind: 'ban', report, name: authorName })}
            >
              Ban member
            </button>
          )}
          {/* Stands where "Ban member" would be, so its absence is explained. */}
          {authorIsCoach && <span className='px-3 py-1.5 text-sm text-gray-400'>Coach account</span>}
        </div>
      )}
    </div>
  )
}

export default ModerationTab
